/**
 * Ligne d'un étudiant pour la prise d'appel.
 * - Swipe droite (> 80px) → présent (vert)
 * - Swipe gauche (> 80px) → absent (rouge)
 * - Tap sur l'avatar / le nom → ouvre le détail (retard, justif, incident)
 * - Boutons rapides ✓ ✗ ⋯ à droite (tablette + clic souris)
 */
import { useRef, useState } from 'react';
import { CheckCircle2, XCircle, Clock, FileCheck, AlertTriangle, MoreHorizontal } from 'lucide-react';
import type { AppelEtudiantItem } from '../../services/vieScolaireApi';

type Statut = AppelEtudiantItem['statut'];

const SEUIL_SWIPE = 80;

function initiales(prenoms: string, nom: string) {
  return ((prenoms?.[0] || '') + (nom?.[0] || '')).toUpperCase() || '?';
}

const META: Record<Statut, { fond: string; bord: string; Icon: any; label: string }> = {
  present:  { fond: 'bg-green-50 dark:bg-green-900/20',  bord: 'border-l-green-500',  Icon: CheckCircle2,    label: 'Présent' },
  retard:   { fond: 'bg-amber-50 dark:bg-amber-900/20',  bord: 'border-l-amber-500',  Icon: Clock,           label: 'Retard' },
  absent:   { fond: 'bg-red-50 dark:bg-red-900/20',      bord: 'border-l-red-500',    Icon: XCircle,         label: 'Absent' },
  justifie: { fond: 'bg-blue-50 dark:bg-blue-900/20',    bord: 'border-l-blue-500',   Icon: FileCheck,       label: 'Justifié' },
  incident: { fond: 'bg-orange-50 dark:bg-orange-900/20',bord: 'border-l-orange-500', Icon: AlertTriangle,   label: 'Incident' },
};

export default function EtudiantAppelRow({
  etudiant, statut, minutesRetard, afficherClasse, onMarquer, onOuvrirDetail,
}: {
  etudiant: AppelEtudiantItem;
  statut: Statut | null;
  minutesRetard: number;
  afficherClasse: boolean;
  onMarquer: (s: Statut) => void;
  onOuvrirDetail: () => void;
}) {
  const [translateX, setTranslateX] = useState(0);
  const startX = useRef<number | null>(null);
  const moving = useRef(false);

  const handleStart = (clientX: number) => {
    startX.current = clientX;
    moving.current = true;
  };
  const handleMove = (clientX: number) => {
    if (!moving.current || startX.current === null) return;
    const dx = clientX - startX.current;
    // Limite à ±120px pour le rendu (mais on déclenche dès SEUIL_SWIPE)
    setTranslateX(Math.max(-120, Math.min(120, dx)));
  };
  const handleEnd = () => {
    if (!moving.current) return;
    moving.current = false;
    if (translateX >= SEUIL_SWIPE) onMarquer('present');
    else if (translateX <= -SEUIL_SWIPE) onMarquer('absent');
    setTranslateX(0);
    startX.current = null;
  };

  // État visuel (fond de la carte)
  const m = statut ? META[statut] : null;

  // Indicateur swipe (fond rouge à gauche, vert à droite)
  const indicateurFond = translateX > 0
    ? 'bg-gradient-to-r from-green-500/30 to-transparent'
    : translateX < 0
      ? 'bg-gradient-to-l from-red-500/30 to-transparent'
      : '';

  return (
    <div className="relative overflow-hidden rounded-lg border border-gray-200 dark:border-gray-700">
      {/* Fond d'indicateur swipe */}
      <div className={`absolute inset-0 ${indicateurFond} flex items-center justify-between px-4 pointer-events-none`}>
        <div className="flex items-center gap-1 text-green-700 dark:text-green-300 text-xs font-semibold opacity-80">
          {translateX > 30 && <><CheckCircle2 size={14} /> Présent</>}
        </div>
        <div className="flex items-center gap-1 text-red-700 dark:text-red-300 text-xs font-semibold opacity-80">
          {translateX < -30 && <>Absent <XCircle size={14} /></>}
        </div>
      </div>

      <div
        className={`relative ${m?.fond || 'bg-white dark:bg-gray-800'} ${m?.bord ? `border-l-4 ${m.bord}` : 'border-l-4 border-l-transparent'} transition-colors`}
        style={{
          transform: `translateX(${translateX}px)`,
          transition: translateX === 0 ? 'transform 0.18s ease-out' : 'none',
          touchAction: 'pan-y',
        }}
        onTouchStart={(e) => handleStart(e.touches[0].clientX)}
        onTouchMove={(e) => handleMove(e.touches[0].clientX)}
        onTouchEnd={handleEnd}
        onMouseDown={(e) => handleStart(e.clientX)}
        onMouseMove={(e) => moving.current && handleMove(e.clientX)}
        onMouseUp={handleEnd}
        onMouseLeave={handleEnd}
      >
        <div className="flex items-center gap-3 p-3">
          {/* Avatar */}
          <button onClick={onOuvrirDetail}
            className="w-10 h-10 rounded-full bg-blue-600 text-white font-bold flex items-center justify-center flex-shrink-0 text-sm">
            {initiales(etudiant.prenoms, etudiant.nom)}
          </button>

          {/* Identité */}
          <button onClick={onOuvrirDetail} className="flex-1 min-w-0 text-left">
            <div className="font-semibold text-sm text-gray-900 dark:text-white truncate">
              {etudiant.nom} {etudiant.prenoms}
            </div>
            <div className="text-[11px] text-gray-500 dark:text-gray-400 flex items-center gap-2 mt-0.5">
              <span>{etudiant.matricule}</span>
              {afficherClasse && etudiant.classe && (
                <span className="px-1.5 py-0.5 bg-gray-100 dark:bg-gray-700 rounded text-[10px] font-medium text-gray-700 dark:text-gray-300">
                  {etudiant.classe}
                </span>
              )}
              {m && (
                <span className="inline-flex items-center gap-1 font-medium">
                  <m.Icon size={11} /> {m.label}{statut === 'retard' && minutesRetard > 0 ? ` (${minutesRetard}')` : ''}
                </span>
              )}
            </div>
          </button>

          {/* Boutons rapides (cachés sur mobile très étroit) */}
          <div className="flex items-center gap-1 flex-shrink-0">
            <button onClick={(e) => { e.stopPropagation(); onMarquer('present'); }}
              className={`p-2 rounded-lg transition ${
                statut === 'present'
                  ? 'bg-green-600 text-white'
                  : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-green-100 dark:hover:bg-green-900/40'
              }`}
              aria-label="Présent">
              <CheckCircle2 size={16} />
            </button>
            <button onClick={(e) => { e.stopPropagation(); onMarquer('absent'); }}
              className={`p-2 rounded-lg transition ${
                statut === 'absent'
                  ? 'bg-red-600 text-white'
                  : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-red-100 dark:hover:bg-red-900/40'
              }`}
              aria-label="Absent">
              <XCircle size={16} />
            </button>
            <button onClick={(e) => { e.stopPropagation(); onOuvrirDetail(); }}
              className="p-2 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600 rounded-lg"
              aria-label="Plus d'options">
              <MoreHorizontal size={16} />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
